import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import Select from "common/components/Select/Select";
import useAuth from "common/hooks/useAuth";
import usePark from "common/hooks/usePark";
import { getParkConfiguration } from "types/Park";

export type ProcessOptionsType = (options: any[]) => {
  value: string;
  label: string;
}[];

function HistoryEventModelSelect(props: any) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { park } = usePark();

  const parkConfiguration = useMemo(
    () => (park ? getParkConfiguration(park) : null),
    [park]
  );

  // discuss if we want to see superadmin here
  const options = useMemo(() => {
    const models = [];
    if (!currentUser) return [];

    const isAtLeastAdmin = currentUser.isSuperadmin || currentUser.isAdmin;

    if (
      isAtLeastAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.animal?.read)
    ) {
      models.push({
        value: "animal",
        label: t("history.modelNamePlural.animal"),
      });
    }
    if (
      isAtLeastAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.species?.read)
    ) {
      models.push({
        value: "species",
        label: t("history.modelNamePlural.species"),
      });
    }
    if (
      isAtLeastAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.genus?.read)
    ) {
      models.push({
        value: "genus",
        label: t("history.modelNamePlural.genus"),
      });
    }
    if (
      isAtLeastAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.superfamily?.read)
    ) {
      models.push({
        value: "superfamily",
        label: t("history.modelNamePlural.superfamily"),
      });
    }
    if (
      isAtLeastAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.enclosure?.read)
    ) {
      models.push({
        value: "enclosure",
        label: t("history.modelNamePlural.enclosure"),
      });
    }
    if (
      isAtLeastAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.sector?.read)
    ) {
      models.push({
        value: "sector",
        label: t("history.modelNamePlural.sector"),
      });
    }
    if (
      isAtLeastAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.tag?.read)
    ) {
      models.push({
        value: "tag",
        label: t("history.modelNamePlural.tag"),
      });
    }

    models.push({
      value: "event",
      label: t("history.modelNamePlural.event"),
    });
    if (
      isAtLeastAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.["observation-group"]?.read)
    ) {
      models.push({
        value: "observation-group",
        label: t("history.modelNamePlural.observation-group"),
      });
    }
    if (
      isAtLeastAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.["observation-grid"]?.read)
    ) {
      models.push({
        value: "observation-grid",
        label: t("history.modelNamePlural.observation-grid"),
      });
    }
    if (
      parkConfiguration?.hasTrainings &&
      (isAtLeastAdmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.["training-grid"]?.read))
    ) {
      models.push({
        value: "training-exercise",
        label: t("history.modelNamePlural.training-exercise"),
      });
      models.push({
        value: "training-request",
        label: t("history.modelNamePlural.training-request"),
      });
      models.push({
        value: "training",
        label: t("history.modelNamePlural.training"),
      });
    }
    if (parkConfiguration?.hasUnavailabilities) {
      models.push({
        value: "unavailability",
        label: t("history.modelNamePlural.unavailability"),
      });
    }
    if (parkConfiguration?.hasModifications) {
      models.push({
        value: "enclosure-update",
        label: t("history.modelNamePlural.enclosure-update"),
      });
    }
    if (parkConfiguration?.hasAssignments) {
      models.push({
        value: "assignment",
        label: t("history.modelNamePlural.assignment"),
      });
    }
    models.push({
      value: "note",
      label: t("history.modelNamePlural.note"),
    });
    return models;
  }, [t, currentUser, parkConfiguration]);

  return <Select options={options} {...props} />;
}

export default HistoryEventModelSelect;
